import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "../lib/api";
import {
  format,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subWeeks,
  subMonths,
} from "date-fns";
import { es } from "date-fns/locale";
import {
  Tags,
  Calendar,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "sonner";
import { API } from "../App";

const defaultSort = {
  sortBy: "net_profit",
  sortOrder: "desc",
};

const sortableColumns = [
  { key: "brand", label: "Marca" },
  { key: "revenue", label: "Ingresos" },
  { key: "product_cost", label: "Coste Producto" },
  { key: "gross_profit", label: "Beneficio Bruto" },
  { key: "gross_margin_pct", label: "Margen Bruto %" },
  { key: "ad_spend", label: "Gasto Publicidad (GAds)" },
  { key: "net_profit", label: "Beneficio Neto" },
  { key: "net_margin_pct", label: "Margen Neto %" },
  { key: "roas", label: "ROAS" },
];

const isNegative = (value) => typeof value === "number" && value < 0;

const formatCurrency = (value) => `€${(value || 0).toLocaleString("es-ES", { minimumFractionDigits: 2 })}`;
const formatPercent = (value) => (value == null ? "—" : `${value.toLocaleString("es-ES", { minimumFractionDigits: 2 })}%`);
const formatRatio = (value) => (value == null ? "—" : `${value.toLocaleString("es-ES", { minimumFractionDigits: 2 })}x`);

export default function AnalyticsBrandsPage() {
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [pendingDateRange, setPendingDateRange] = useState({ from: null, to: null });
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 50, total_rows: 0, total_pages: 1 });
  const [sortState, setSortState] = useState(defaultSort);
  const [loading, setLoading] = useState(false);
  const [dataQuality, setDataQuality] = useState({
    unbranded_sales_lines_excluded: 0,
    unmatched_google_ads_cost: 0,
    sin_marca_ad_spend: 0,
    eliminados_ad_spend: 0,
    unmapped_products_sample: [],
    gads_match_stats: null,
  });
  const [adSpendSource, setAdSpendSource] = useState(null);

  const hasDateRange = Boolean(dateRange.from && dateRange.to);

  const canGoPrev = pagination.page > 1;
  const canGoNext = pagination.page < pagination.total_pages;

  const quickPresets = useMemo(
    () => [
      { label: "Hoy", from: new Date(), to: new Date() },
      { label: "Ayer", from: subDays(new Date(), 1), to: subDays(new Date(), 1) },
      { label: "Últimos 7 días", from: subDays(new Date(), 6), to: new Date() },
      { label: "Últimos 14 días", from: subDays(new Date(), 13), to: new Date() },
      { label: "Últimos 30 días", from: subDays(new Date(), 29), to: new Date() },
      { label: "Esta semana", from: startOfWeek(new Date(), { weekStartsOn: 1 }), to: new Date() },
      { label: "Semana pasada", from: startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }), to: endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }) },
      { label: "Este mes", from: startOfMonth(new Date()), to: new Date() },
      { label: "Mes pasado", from: startOfMonth(subMonths(new Date(), 1)), to: endOfMonth(subMonths(new Date(), 1)) },
    ],
    []
  );

  const fetchData = async (targetPage = pagination.page) => {
    if (!dateRange.from || !dateRange.to) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/analytics/brands-profitability`, {
        params: {
          start_date: format(dateRange.from, "yyyy-MM-dd"),
          end_date: format(dateRange.to, "yyyy-MM-dd"),
          search: search.trim() || undefined,
          sort_by: sortState.sortBy,
          sort_order: sortState.sortOrder,
          page: targetPage,
          page_size: 50,
        },
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      setRows(response.data.rows || []);
      setPagination(response.data.pagination || { page: 1, page_size: 50, total_rows: 0, total_pages: 1 });
      setDataQuality(response.data.data_quality || {
        unbranded_sales_lines_excluded: 0,
        unmatched_google_ads_cost: 0,
        sin_marca_ad_spend: 0,
        eliminados_ad_spend: 0,
        unmapped_products_sample: [],
        gads_match_stats: null,
      });
      setAdSpendSource(response.data?.sources?.ad_spend || null);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error cargando rentabilidad por marca";
      toast.error(errorMsg);
      setRows([]);
      setDataQuality({
        unbranded_sales_lines_excluded: 0,
        unmatched_google_ads_cost: 0,
        sin_marca_ad_spend: 0,
        eliminados_ad_spend: 0,
        unmapped_products_sample: [],
        gads_match_stats: null,
      });
      setAdSpendSource(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasDateRange) return;
    fetchData(1);
  }, [hasDateRange, dateRange, search, sortState]);

  const onSort = (columnKey) => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSortState((prev) => {
      if (prev.sortBy === columnKey) {
        return {
          ...prev,
          sortOrder: prev.sortOrder === "asc" ? "desc" : "asc",
        };
      }
      return {
        sortBy: columnKey,
        sortOrder: "desc",
      };
    });
  };

  const renderSortIcon = (columnKey) => {
    if (sortState.sortBy !== columnKey) return <ArrowUpDown className="w-3.5 h-3.5" />;
    return sortState.sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="analytics-brands-page">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center" data-testid="brands-page-icon">
              <Tags className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-slate-900" data-testid="brands-page-title">Rentabilidad por Marca</h1>
              <p className="text-slate-600" data-testid="brands-page-subtitle">
                Vista multi-tenant y agnóstica por tienda, basada en datos internos normalizados.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2" data-testid="analytics-submenu-links">
            <Button variant="outline" asChild data-testid="analytics-ga4-link-button">
              <Link to="/analytics">GA4</Link>
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" asChild data-testid="analytics-brands-link-button">
              <Link to="/analytics/brands">Marcas</Link>
            </Button>
            <Button variant="outline" asChild data-testid="analytics-products-link-button">
              <Link to="/analytics/products">Productos</Link>
            </Button>
          </div>
        </div>

        <Card className="bg-white border border-slate-200" data-testid="brands-controls-card">
          <CardContent className="p-4 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <Popover open={datePopoverOpen} onOpenChange={(open) => { setDatePopoverOpen(open); if (open) setPendingDateRange(dateRange); }}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="border-slate-200" data-testid="brands-date-picker-button">
                    <Calendar className="w-4 h-4 mr-2" />
                    {hasDateRange
                      ? `${format(dateRange.from, "d MMM", { locale: es })} - ${format(dateRange.to, "d MMM yyyy", { locale: es })}`
                      : "Selecciona rango de fechas"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <div className="flex">
                    <div className="border-r border-slate-200 p-2 space-y-1 min-w-[180px]">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 py-1">Accesos rápidos</p>
                      {quickPresets.map((preset) => (
                        <button
                          key={preset.label}
                          onClick={() => {
                            const range = { from: preset.from, to: preset.to };
                            setDateRange(range);
                            setPendingDateRange(range);
                            setDatePopoverOpen(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                          data-testid={`brands-date-preset-${preset.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                    <div className="p-2 flex flex-col">
                      <CalendarComponent
                        mode="range"
                        selected={pendingDateRange}
                        onSelect={(range) => setPendingDateRange({ from: range?.from || null, to: range?.to || null })}
                        numberOfMonths={2}
                        locale={es}
                      />
                      <div className="border-t border-slate-200 pt-2 mt-1 flex justify-end">
                        <Button
                          size="sm"
                          className="bg-red-600 hover:bg-red-700 text-white"
                          disabled={!pendingDateRange.from}
                          onClick={() => {
                            const range = { from: pendingDateRange.from, to: pendingDateRange.to || pendingDateRange.from };
                            setDateRange(range);
                            setPendingDateRange(range);
                            setDatePopoverOpen(false);
                          }}
                        >
                          Aplicar
                        </Button>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div className="relative w-full lg:w-[320px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => {
                  setPagination((prev) => ({ ...prev, page: 1 }));
                  setSearch(e.target.value);
                }}
                placeholder="Buscar marca..."
                className="pl-9"
                data-testid="brands-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {!hasDateRange ? (
          <Card className="bg-amber-50 border border-amber-200" data-testid="brands-date-range-required-card">
            <CardContent className="p-6">
              <p className="text-amber-900 font-semibold" data-testid="brands-date-range-required-title">
                Selecciona un rango de fechas para calcular la rentabilidad por marca
              </p>
              <p className="text-amber-700 text-sm mt-1" data-testid="brands-date-range-required-description">
                Sin rango seleccionado no se ejecutan cálculos, cumpliendo la regla de negocio.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white border border-slate-200" data-testid="brands-table-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-bold text-slate-900" data-testid="brands-table-title">
                Tabla de Rentabilidad por Marca
              </CardTitle>
              <p className="text-sm text-slate-500" data-testid="brands-table-meta">
                {loading ? "Cargando..." : `${pagination.total_rows || 0} marcas encontradas`}
              </p>
              <p className="text-xs text-slate-500" data-testid="brands-ad-spend-source">
                Fuente gasto Google Ads: {adSpendSource || "no disponible"}
              </p>
            </CardHeader>
            <CardContent>
              {/* Avisos de calidad de datos — accionables */}
              {dataQuality.unbranded_sales_lines_excluded > 0 && (
                <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="brands-data-quality-unbranded-sales">
                  {dataQuality.unbranded_sales_lines_excluded} líneas de venta sin marca asignada han sido excluidas.
                  Asigna una marca a esos productos en tu tienda para incluirlos en el análisis.
                </div>
              )}
              {dataQuality.sin_marca_ad_spend > 0 && (
                <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="brands-data-quality-sin-marca">
                  <span className="font-semibold">
                    {dataQuality.gads_match_stats?.sin_marca ?? "?"} producto{dataQuality.gads_match_stats?.sin_marca !== 1 ? "s" : ""} sin marca asignada
                  </span>{" "}
                  generan <span className="font-semibold">€{dataQuality.sin_marca_ad_spend.toLocaleString("es-ES", { minimumFractionDigits: 2 })}</span> de
                  gasto Google Ads — asigna una marca en tu tienda para incluirlos en el análisis.
                  {dataQuality.sin_marca_products_sample?.length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs text-amber-700 underline">Ver muestra de IDs sin marca (top {dataQuality.sin_marca_products_sample.length} por gasto)</summary>
                      <table className="mt-2 text-xs w-full">
                        <thead>
                          <tr className="text-left text-amber-800">
                            <th className="pr-4 font-semibold">product_item_id (Google Ads)</th>
                            <th className="text-right font-semibold">Gasto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dataQuality.sin_marca_products_sample.map((s) => (
                            <tr key={s.product_item_id} className="border-t border-amber-200">
                              <td className="pr-4 font-mono py-0.5">{s.product_item_id}</td>
                              <td className="text-right font-mono">€{s.ad_spend.toLocaleString("es-ES", { minimumFractionDigits: 2 })}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </details>
                  )}
                </div>
              )}
              {dataQuality.eliminados_ad_spend > 0 && (
                <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900" data-testid="brands-data-quality-eliminados">
                  <span className="font-semibold">
                    {dataQuality.gads_match_stats?.eliminados ?? "?"} producto{dataQuality.gads_match_stats?.eliminados !== 1 ? "s" : ""} eliminados de tu tienda
                  </span>{" "}
                  generan <span className="font-semibold">€{dataQuality.eliminados_ad_spend.toLocaleString("es-ES", { minimumFractionDigits: 2 })}</span> de
                  gasto activo en Google Ads — considera pausarlos.
                  {dataQuality.unmapped_products_sample?.length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs text-red-700 underline">Ver muestra de IDs no mapeados</summary>
                      <ul className="mt-1 space-y-0.5 font-mono text-xs">
                        {dataQuality.unmapped_products_sample.map((s) => (
                          <li key={s.product_item_id}>
                            <span className="text-red-800">{s.product_item_id}</span>{" "}
                            <span className="text-red-600">— €{s.ad_spend.toLocaleString("es-ES", { minimumFractionDigits: 2 })}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
              <div className="overflow-x-auto" data-testid="brands-table-wrapper">
                <Table data-testid="brands-profitability-table">
                  <TableHeader>
                    <TableRow>
                      {sortableColumns.map((col) => (
                        <TableHead key={col.key} className={col.key === "brand" ? "text-left" : "text-right"}>
                          <button
                            type="button"
                            onClick={() => onSort(col.key)}
                            className={`inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 ${col.key === "brand" ? "justify-start" : "justify-end w-full"}`}
                            data-testid={`brands-sort-${col.key}`}
                          >
                            <span>{col.label}</span>
                            {renderSortIcon(col.key)}
                          </button>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!loading && rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center text-slate-500 py-10" data-testid="brands-empty-state">
                          No hay datos para el rango/filtros seleccionados.
                        </TableCell>
                      </TableRow>
                    )}

                    {rows.map((row, idx) => {
                      const isSpecial = row.is_special || row.brand_id?.startsWith("__");
                      const rowClass = isSpecial
                        ? row.brand_id === "__eliminados__"
                          ? "bg-red-50 border-l-4 border-l-red-400"
                          : "bg-amber-50 border-l-4 border-l-amber-400"
                        : "";
                      return (
                        <TableRow key={`${row.brand_id || row.brand}-${idx}`} className={rowClass} data-testid={`brands-row-${idx}`}>
                          <TableCell className={`font-semibold ${isSpecial ? "italic" : "text-slate-900"}`} data-testid={`brands-row-${idx}-brand`}>
                            {row.brand}
                          </TableCell>
                          <TableCell className={`text-right font-mono ${isNegative(row.revenue) ? "text-red-600" : "text-slate-700"}`} data-testid={`brands-row-${idx}-revenue`}>
                            {formatCurrency(row.revenue)}
                          </TableCell>
                          <TableCell className={`text-right font-mono ${isNegative(row.product_cost) ? "text-red-600" : "text-slate-700"}`} data-testid={`brands-row-${idx}-product-cost`}>
                            {formatCurrency(row.product_cost)}
                          </TableCell>
                          <TableCell className={`text-right font-mono ${isNegative(row.gross_profit) ? "text-red-600" : "text-slate-700"}`} data-testid={`brands-row-${idx}-gross-profit`}>
                            {formatCurrency(row.gross_profit)}
                          </TableCell>
                          <TableCell className={`text-right font-mono ${isNegative(row.gross_margin_pct) ? "text-red-600" : "text-slate-700"}`} data-testid={`brands-row-${idx}-gross-margin`}>
                            {formatPercent(row.gross_margin_pct)}
                          </TableCell>
                          <TableCell className={`text-right font-mono ${isNegative(row.ad_spend) ? "text-red-600" : "text-slate-700"}`} data-testid={`brands-row-${idx}-ad-spend`}>
                            {formatCurrency(row.ad_spend)}
                          </TableCell>
                          <TableCell className={`text-right font-mono font-bold ${isNegative(row.net_profit) ? "text-red-600" : "text-slate-900"}`} data-testid={`brands-row-${idx}-net-profit`}>
                            {formatCurrency(row.net_profit)}
                          </TableCell>
                          <TableCell
                            className={`text-right font-mono ${isNegative(row.net_margin_pct) ? "text-red-700 bg-red-50" : "text-slate-700"}`}
                            data-testid={`brands-row-${idx}-net-margin`}
                          >
                            {formatPercent(row.net_margin_pct)}
                          </TableCell>
                          <TableCell className={`text-right font-mono ${isNegative(row.roas) ? "text-red-600" : "text-slate-700"}`} data-testid={`brands-row-${idx}-roas`}>
                            {formatRatio(row.roas)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {pagination.total_rows > 50 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4" data-testid="brands-pagination">
                  <p className="text-sm text-slate-500" data-testid="brands-pagination-summary">
                    Página {pagination.page} de {pagination.total_pages}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (!canGoPrev) return;
                        const nextPage = pagination.page - 1;
                        setPagination((prev) => ({ ...prev, page: nextPage }));
                        fetchData(nextPage);
                      }}
                      disabled={!canGoPrev || loading}
                      data-testid="brands-pagination-prev"
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (!canGoNext) return;
                        const nextPage = pagination.page + 1;
                        setPagination((prev) => ({ ...prev, page: nextPage }));
                        fetchData(nextPage);
                      }}
                      disabled={!canGoNext || loading}
                      data-testid="brands-pagination-next"
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}